import type { Tekstdok } from "@infoflyt/shared/tekst";
import type { TcAnchor, TcState } from "@infoflyt/shared/timecode";

/**
 * En loggpost. Lagrer øyeblikket den ble satt, aldri tidskoden.
 *
 * Det finnes ingen updated_at: tekstendringer går i audit_log, slik at
 * endringshistorikken ligger ett sted og ikke to.
 */
export interface LogEntry {
  readonly id: string;
  /** Produksjonen posten hoerer til, eller «privat». Se Production. */
  readonly productionId: string;
  readonly sessionId: string;
  readonly anchorId: string;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
  /** Hvilken hurtigknapp posten kom fra. Null for prosaposter. */
  readonly buttonId: string | null;
  /**
   * Deltakeren en SYNK-post hoerer til. Null for alt annet.
   *
   * Posten dekker HELE synken — ett merke, mange spoersmaal — saa hvilke
   * spoersmaal som ble stilt staar i `SuSvar`, ikke her. Deltakeren staar
   * her fordi en synk er med én person, og det er verdt aa kunne spoerre om
   * direkte.
   */
  readonly participantId: string | null;
  /** Knappens etikett, frosset i trykkøyeblikket. Tom for prosaposter. */
  readonly label: string;
  readonly color: string | null;
  readonly text: string;
  readonly tcState: TcState;
  /** Sletting er tombstone, aldri fysisk. */
  readonly deletedAt: number | null;
  /**
   * Forkortelsen til den som skrev posten, naar det ikke var deg.
   *
   * Null paa alt du har skrevet selv — og det er de aller fleste postene.
   * Teamet deler logg fra 29.10.2026, og da henter appen de andres poster
   * ned naar den er paa nett. En post med forkortelse er EN ANNENS:
   *
   *   * Den kan ikke rettes eller slettes her. «En loggpost slettes bare
   *     der den ble skrevet, av den som skrev den» staar urort — det som
   *     endret seg er hvem loggen tilhoerer, ikke hvem posten tilhoerer.
   *   * Den ligger ikke i utboksen. Den kom NED; den skal ikke opp igjen.
   */
  readonly skrevetAv: string | null;
}

export interface StoredAnchor extends TcAnchor {
  readonly id: string;
  readonly productionId: string;
  readonly shootDay: string;
  /**
   * Ankeret dette er AVLEDET av, eller null.
   *
   * Null betyr at noen har JAMMET — satt tidskoden med hånda — og det er
   * slike ankere som gjelder for produksjonen. Er man paa samme produksjon,
   * har man samme tidskode: jammer én, har alle satt den.
   *
   * Et avledet anker baerer den samme tidskoden, men uttrykt paa DENNE
   * enhetens klokke, slik `reanchorAfterReboot` gjoer etter en omstart. Da
   * stemmer verdien med resten av produksjonen, og enhetens egne poster
   * staar fortsatt som `ok` — flagget betyr «den monotone tellingen brast»,
   * og det skal det fortsette aa bety. Se migrasjon 20261025140000.
   */
  readonly derivedFrom: string | null;
  /**
   * Hva slags anker dette er.
   *
   * `jam`     — satt mot en ekte referanse. Teknikerens handling, og den
   *             gjelder HELE produksjonen; de andre enhetene adopterer den.
   * `klokke`  — enheten startet paa sin egen klokke fordi det ikke fantes
   *             noen jam. Foreloepig: gjelder bare denne enheten.
   * `avledet` — en adopsjon, uttrykt paa denne enhetens klokke.
   *
   * Fram til 27.10.2026 fantes ikke skillet, og `derivedFrom === null` betoed
   * «jam». Da ble regis telefonklokke produksjonens tidskode bare ved at
   * appen ble aapnet. Se migrasjon 20261027091000.
   */
  readonly kind: "jam" | "klokke" | "avledet";
  /**
   * Naar JAMMEN ble satt. Null naar ingen har jammet.
   *
   * ========  HVORFOR `wallUtcMs` IKKE DUGER  ========
   *
   * Det feltet er naar DETTE ankeret ble skrevet. Paa et adoptert anker
   * er det naar vi adopterte, ikke naar teknikeren jammet — saa en jam
   * satt for tretti timer siden, adoptert naa, ville staatt som fersk.
   * Det er feil vei for en advarsel.
   *
   * Tidspunktet baeres derfor gjennom adopsjonen og gjennom en omstart.
   * Brukeren 01.11.2026: «Hvis tidskoden ikke er jammet av en tekniker
   * de siste 24 timene, saa skal tidskoden vaere roed. Hvis den ikke er
   * jammet i det heletatt, saa skal den vises som streker.»
   */
  readonly jammetUtcMs: number | null;
}

export interface LogButton {
  readonly id: string;
  readonly label: string;
  readonly color: string;
  readonly sortOrder: number;
}

export type PageId =
  | "logg" | "hurtiglogg" | "su" | "timer"
  | "dagsmanus" | "nedbrekk" | "dagsplan" | "kjoreplan" | "kameraplan"
  | "brief" | "shotlist";

export const PAGE_NAMES: { readonly [K in PageId]: string } = {
  logg: "Logg",
  hurtiglogg: "Hurtiglogg",
  su: "SU/synk",
  timer: "Timer",
  dagsmanus: "Dagsmanus",
  nedbrekk: "Nedbrekk",
  dagsplan: "Dagsplan",
  kjoreplan: "Kjøreplan",
  kameraplan: "Kameraplan",
  brief: "Brief",
  shotlist: "Shotlist",
};

/**
 * Nøkkelen briefen ligger under. Se `text_page.slug`.
 *
 * Sida er fast som dagsplanen, ikke et felt prodleder finner på — derfor en
 * konstant her og ikke et oppslag.
 */
export const BESKJED_SLUGER = ["brief-teknisk", "brief-innhold", "brief-prod"] as const;


/**
 * Standardrekkefølgen. Brukeren drar den om som hen vil.
 *
 * SU/synk ligger ved loggsidene og ikke ved dokumentene: det er en side man
 * SKRIVER på — en avhuking setter en markør i loggen — og den brukes i samme
 * situasjon som Hurtiglogg.
 */
export const DEFAULT_PAGE_ORDER: readonly PageId[] = [
  "logg",
  "hurtiglogg",
  "su",
  "timer",
  "dagsmanus",
  "nedbrekk",
  "dagsplan",
  "kjoreplan",
  "kameraplan",
  "brief",
  "shotlist",
];

/**
 * En person foran kamera.
 *
 * Forkortelsen er det som faktisk skrives i loggen — ALI for Alina. Den er
 * registrert per produksjon og skrives ikke fritt, slik at koblingen kan
 * etableres i etterkant uten opprydding. Se docs/05-modul-logg.md.
 */
/**
 * En flate som SKRIVES paa nettflata og leses her.
 *
 * Brief for teknisk crew er den foerste. Ulikt dagsplanen er dette ikke en
 * opplastet fil: en tekst kan rettes med to ord, og den veier ingenting.
 */
export interface Tekstflate {
  readonly id: string;
  readonly slug: string;
  readonly tittel: string;
  readonly dok: Tekstdok;
  readonly endretIso: string;
}

export interface Deltaker {
  readonly id: string;
  readonly navn: string;
  readonly forkortelse: string;
}

/**
 * Et spoersmaal som skal stilles i SU eller synk.
 *
 * `deltakerId` null betyr GENERELT: det stilles til alle, og ligger oeverst
 * paa hver deltakers liste under sin egen tittel.
 */
/**
 * Ett avhuket spoersmaal.
 *
 * HAKEN ER IKKE EN TILSTAND HER HELLER. Raden peker paa loggposten, og et
 * spoersmaal er stilt hvis posten LEVER. Slettes SYNK-posten fra
 * Logg-skjermen, faller hele synken ut av sjekklista — uten at noe maa
 * holdes i takt.
 */
export interface SuSvar {
  readonly id: string;
  readonly entryId: string;
  readonly spoersmaalId: string;
  readonly deltakerId: string;
  readonly atUtcMs: number;
  /**
   * Hva de svarte. Tom til noen skriver det ned.
   *
   * SKRIVES OFTEST I ETTERTID, og kan rettes — man husker feil, man hoerer
   * gjennom opptaket, man strammer inn. Det er forskjellen fra loggteksten,
   * som er append-only. Lagt til 24.10.2026, med reporterens synkflate.
   */
  readonly svar: string;
  /** Naar svaret ble skrevet. Null saa lenge det ikke er skrevet. */
  readonly svarAtMs: number | null;
}

/**
 * Loggposten redaktoeren skrev spoersmaalet UT AV.
 *
 * Klipperen skal kunne finne fram innholdet spoersmaalet hoerer til, og da er
 * det tre ting hen trenger: tidskoden, posten og rapporten. Derfor ligger de
 * her, som en kopi av en annens logg — posten staar som regel i en annen
 * regissoers oekt, og den har denne telefonen aldri sett.
 *
 * TIDSKODEN SOM FRAMES, aldri som streng. Ankeret kan justeres i etterkant,
 * og da flytter tallet seg ved neste synk. Se CLAUDE.md.
 */
export interface Spoersmaalskilde {
  readonly entryId: string;
  readonly tcFrames: number;
  readonly framerate: string;
  readonly dropFrame: boolean;
  readonly opptaksdag: string;
  /** Postens tekst. Det klipperen kjenner igjen. */
  readonly tekst: string;
  /** Hoeyeste leverte rapportversjon for oekta, eller null. */
  readonly rapportversjon: number | null;
}

export interface SuSpoersmaal {
  readonly id: string;
  readonly deltakerId: string | null;
  readonly tekst: string;
  readonly sortOrder: number;
  /**
   * Naar spoersmaalet ble skrevet. Avgjoer om det er blitt en PICKUP.
   *
   * Null paa rader fra foer 26.10.2026 — da vet vi det ikke, og da flyttes
   * de ikke. Regelen ligger i packages/shared/synk.
   */
  readonly skrevetMs: number | null;
  /**
   * Opptaksdagen det ble SKREVET paa.
   *
   * Staar som knagg i rapporten, slik at redaktoer og klipper finner
   * materialet spoersmaalet hoerer til. `created_at` kan ikke svare paa
   * det: et skift som gaar over midnatt hoerer fortsatt til dagen det
   * startet, og dagen er en etikett produksjonen setter.
   */
  readonly opptaksdag: string | null;
  /** Satt naar redaktoeren skrev spoersmaalet ut av en loggpost. */
  readonly kilde?: Spoersmaalskilde | null;
}

/**
 * Et felt prodleder har laget selv — en turnus, et kart, en kameraplan.
 *
 * TITTELEN ER IDENTITETEN, og det er ikke et valg vi tar her: nettflata slaar
 * alt egendefinert opp paa tittel, fordi alle har slaget `annet` og to av dem
 * ellers ville truffet hverandre (`serieFor` i apps/web/src/state/dokumenter).
 * Appen bruker derfor det samme. Doeper prodleder om feltet, blir det et nytt
 * felt begge steder — og det er den samme sannheten, ikke to.
 */
/**
 * Sidene som viser et OPPLASTET DOKUMENT.
 *
 * Lista er filteret i App: et dokument med et slag som ikke staar her,
 * lastes ned og kastes uten at noe ser galt ut noe sted. Det hullet var her
 * én gang foer, for «Legg til felt».
 *
 * Slagene heter det samme som sidene med vilje — `document_series.kind` er
 * noeyaktig disse strengene.
 */
export const DOKUMENTSIDER = [
  "dagsmanus", "nedbrekk", "dagsplan", "kjoreplan", "kameraplan",
] as const satisfies readonly PageId[];

/** To navn er det samme navnet. Store bokstaver og luft teller ikke. */
export function sammeNavn(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase("nb") === b.trim().toLocaleLowerCase("nb");
}

/**
 * ETT FELT HETER ÉN TING.
 *
 * Et felt prodleder lagde selv kan hete det samme som en fast dokumentside —
 * oftest fordi det faste slaget kom til ETTERPAA. «Kameraplan» var et
 * egendefinert felt foer det ble et slag, og da sto den gamle serien igjen
 * ved siden av den nye: to sider med samme navn, der bare den ene hadde fila.
 *
 * Nettflata loeser det samme der (`flett` i OpplastingScreen), og de to maa
 * vaere enige om hva som er «det samme feltet».
 */
export function erDokumentsidenavn(tittel: string): boolean {
  return DOKUMENTSIDER.some((p) => sammeNavn(tittel, PAGE_NAMES[p]));
}

export type FeltId = `felt:${string}`;
export type SideId = PageId | FeltId;

const FELT_PREFIKS = "felt:";

export const feltId = (tittel: string): FeltId => `${FELT_PREFIKS}${tittel}`;
export const erFelt = (id: SideId): id is FeltId => id.startsWith(FELT_PREFIKS);
export const feltTittel = (id: FeltId): string => id.slice(FELT_PREFIKS.length);

/** Navnet som vises. De faste har et fast navn, feltene heter det de heter. */
export function sidenavn(id: SideId): string {
  return erFelt(id) ? feltTittel(id) : PAGE_NAMES[id];
}

export interface PagePreference {
  readonly page: SideId;
  readonly enabled: boolean;
  readonly sortOrder: number;
}

/** Ren registrering. Ingen beregning — se docs/07-modul-timefoering.md. */
export interface TimeEntry {
  readonly id: string;
  readonly productionId: string;
  readonly startedAtUtcMs: number;
  readonly stoppedAtUtcMs: number | null;
  /*
   * Monoton avlesning da dagen ble startet.
   *
   * Forløpt tid regnes aldri fra veggklokka — operativsystemet justerer den
   * uten varsel, og et hopp midt i skiftet flytter tallet regi fører timer
   * etter. Med disse to kan den løpende varigheten regnes på samme klokke
   * som loggposten bruker.
   *
   * `null` på økter lagret før feltene fantes, og på økter der starttiden er
   * satt for hånd: da finnes det ingen måling å regne fra, og veggklokka er
   * det eneste vi har. Se docs/04-timecode.md.
   */
  readonly monotonicMs: number | null;
  readonly bootId: string | null;
  readonly note: string;
  readonly source: "app" | "manual";
}

export type AuditAction =
  | "anchor_adjusted"
  /**
   * Produksjonens jam ble adoptert.
   *
   * Noen andre satte tidskoden, og denne enheten tok den i bruk. Egen
   * handling og ikke `anchor_adjusted`: ingen her har rort noe, og den som
   * leser loggen i ettertid skal kunne se forskjell paa at NOEN justerte og
   * at vi FULGTE ETTER. Lagt til 25.10.2026.
   */
  | "anchor_adopted"
  /** Framerate endret paa «privat». En produksjons framerate settes av prodleder. */
  | "framerate_changed"
  | "entry_text_edited"
  | "entry_deleted"
  | "report_closed";

export interface AuditRecord {
  readonly id: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly action: AuditAction;
  readonly before: string | null;
  readonly after: string | null;
  readonly createdAtUtcMs: number;
}

/** Produksjonen appen er logget inn paa. Speiler `production` i skjemaet. */
export interface Production {
  readonly id: string;
  readonly name: string;
  readonly framerate: "23.976" | "24" | "25" | "29.97" | "30" | "50" | "59.94" | "60";
  readonly dropFrame: boolean;
  /**
   * Styrer automatisk sletting av lokale data.
   *
   * NULL betyr ingen automatisk sletting, og BARE `privat` har det. Den
   * private loggen hoerer ikke til en produksjon og kan ikke arve en annens
   * frist. For enhver ekte produksjon er feltet satt — `end_date` er
   * `NOT NULL` i skjemaet, og det staar.
   *
   * Alternativet var aa gi privat en fiktiv sluttdato langt fram. Det er en
   * loegn noen kommer til aa stole paa senere.
   */
  readonly endDateIso: string | null;
  /** Klippeprogrammet prodleder har satt. Null betyr at bare PDF leveres. */
  readonly nle: "avid" | "premiere" | "resolve" | null;
  /**
   * Du er ikke med i produksjonen lenger.
   *
   * Raden blir staaende saa lenge noe ULEVERT ligger her — poster som
   * aldri naadde serveren er ditt eget arbeid og finnes ingen andre
   * steder. Alt annet er slettet, og ingenting kan skrives. Fristen er
   * den samme som ellers: `endDateIso`. Se db.slettProduksjonLokalt.
   */
  readonly utenTilgang?: boolean;
  /**
   * Logoen som gjelder — FERDIG VALGT.
   *
   * Produksjonens gaar foran selskapets; regelen er `velgLogo` i
   * `@infoflyt/shared/logo`, og den brukes én gang naar oekten bygges.
   * Null betyr ingen logo, og da staar Infoflyt-merket.
   *
   * Dette er en STI, ikke en lenke. Fila lastes ned én gang og ligger i
   * `state/logolager.ts` — appen brukes uten dekning, og en signert lenke
   * varer en time.
   */
  readonly logoSti?: string | null;
  readonly units: readonly string[];
}

/** Den innloggede, i denne produksjonen. Speiler `production_member`. */
export interface Member {
  readonly id: string;
  readonly displayName: string;
  readonly initials: string;
  readonly role:
    | "regi" | "vj" | "redaktor" | "produsent" | "klipper" | "prodleder" | "crew"
    /* Synkene. Rolle og ikke fagfunksjon, fordi reporteren SKRIVER noe. */
    | "reporter"
    /* Teknisk-gruppa — foto, A-foto, A-lyd, TOM, lyd, grip, shader, teknisk
       assistent. Det er disse som jammer tidskoden, og derfor ble de en
       rolle 27.10.2026. Se migrasjon 20261027090000. */
    | "tekniker"
    /* A-foto: jammer, og eier takten og utstyrslista. Se 20261027190000. */
    | "afoto"
    /* Koordinatoren laster opp dagsplanen. Rolle fra 31.10.2026, etter
       samme proeve: hen SKRIVER noe. Se 20261031100000. */
    | "koordinator";
  /**
   * Rollene de EKSTRA fagfunksjonene gir.
   *
   * En person har en rad per produksjon, men kan ha flere
   * fagfunksjoner paa den — koordinator og prodleder, for eksempel.
   * Basen regner dem i `tilgang.har_rolle`; appen maa kjenne dem for
   * aa vite om jam-knappen finnes. Se migrasjon 20261101110000.
   */
  readonly extraRoles?: readonly Member["role"][];
}

export interface Session {
  readonly production: Production;
  readonly member: Member;
  /*
   * Opptaksdag og enhet hoerer til loggoekten, ikke til produksjonen — samme
   * produksjon har mange dager og flere enheter. De er flate og ikke samlet i
   * et eget objekt, fordi det objektet ville trengt et navn, og det eneste
   * naturlige — `shift` — er pensjonert i datamodellen med vilje.
   */
  readonly shootDay: string;
  readonly unitName: string;
  /**
   * Teamets id, eller tom streng.
   *
   * Navnet alene holder ikke lenger: oekt-id-en utledes av (produksjon,
   * dag, TEAM-ID), og den maa vaere tilgjengelig OFFLINE. Derfor foelger
   * den med ned fra `production_member` ved synk, i stedet for aa slaas
   * opp naar den trengs. Se state/fagfunksjon.ts og 20261029090000.
   */
  readonly unitId: string;
}
